import { DestroyRef, inject, Injectable, signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { SearchResults, SearchService } from '@shared/services/search.service';
import { debounceTime, of, switchMap, tap } from 'rxjs';

@Injectable()
export abstract class AbstractSearchComponent {
  private destroyRef = inject(DestroyRef);
  private fb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private searchService = inject(SearchService);

  abstract dropdown: any;

  form: UntypedFormGroup;
  searchResults: WritableSignal<SearchResults> = signal(null);
  showSearch: WritableSignal<boolean> = signal(false);
  isSearching: WritableSignal<boolean>  = signal(false);

  constructor() {
    this.initForm();
  }

  get queryControl(): UntypedFormControl {
    return this.form.get('query') as UntypedFormControl;
  }

  clearSearch(): void {
    this.searchResults.set(null);
    this.showSearch.set(false);

    this.form.reset({}, { emitEvent: false });
  }

  onInputFocus(): void {
    if (this.isSearching() || !this.showSearch()) return;

    this.dropdown.open();
  }

  onSubmit(): void {
    this.router.navigate([ '/search' ], { queryParams: { query: this.queryControl.value } });
    this.clearSearch();
  }

  private initForm(): void {
    this.form = this.fb.group({
      query: [ '', [ Validators.required ] ],
    });

    this.queryControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.isSearching.set(true)),
        debounceTime(400),
        switchMap((query: string) => this.queryControl.valid ? this.searchService.getResults(query) : of(null)),
      )
      .subscribe({
        next: (result: SearchResults) => {
          this.searchResults.set(result);
          this.isSearching.set(false);
          this.showSearch.set(true);
          this.dropdown.open();
        },
      });
  }
}
